import React, { useMemo } from "react";
import { CheckoutProcessStatusType, useCheckoutForm } from "./hooks/useCheckoutForm";
import { Stack, Typography, Button, Box } from "@mui/material";
import { XIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { AppConfigFields } from "../../types/appConfigEntities";
import { WhatsApp } from "@mui/icons-material";

// Partículas de confetti con valores fijos para evitar jitter en re-renders
const PARTICLES: Array<{
    l: number;
    c: string;
    w: number;
    h: number;
    d: number;
    t: number;
    r: number;
}> = [
    { l: 5, c: "#f59e0b", w: 8, h: 5, d: 0, t: 2.2, r: 15 },
    { l: 12, c: "#3b82f6", w: 5, h: 10, d: 0.1, t: 1.9, r: -30 },
    { l: 20, c: "#ef4444", w: 9, h: 6, d: 0.05, t: 2.5, r: 45 },
    { l: 28, c: "#8b5cf6", w: 7, h: 7, d: 0.3, t: 2.0, r: -15 },
    { l: 35, c: "#ec4899", w: 5, h: 9, d: 0.15, t: 2.3, r: 60 },
    { l: 42, c: "#10b981", w: 8, h: 5, d: 0.4, t: 1.8, r: -45 },
    { l: 50, c: "#f59e0b", w: 6, h: 8, d: 0.2, t: 2.1, r: 30 },
    { l: 57, c: "#3b82f6", w: 9, h: 6, d: 0.35, t: 2.4, r: -60 },
    { l: 64, c: "#ef4444", w: 5, h: 10, d: 0.1, t: 2.0, r: 20 },
    { l: 72, c: "#8b5cf6", w: 8, h: 5, d: 0.25, t: 1.9, r: -20 },
    { l: 80, c: "#ec4899", w: 7, h: 7, d: 0.45, t: 2.3, r: 70 },
    { l: 88, c: "#10b981", w: 5, h: 9, d: 0.05, t: 2.1, r: -40 },
    { l: 94, c: "#f59e0b", w: 8, h: 5, d: 0.5, t: 2.2, r: 10 },
    { l: 9, c: "#3b82f6", w: 6, h: 7, d: 0.55, t: 2.0, r: -55 },
    { l: 17, c: "#ec4899", w: 5, h: 8, d: 0.6, t: 1.9, r: 40 },
    { l: 25, c: "#ef4444", w: 9, h: 6, d: 0.5, t: 2.5, r: -25 },
    { l: 33, c: "#8b5cf6", w: 7, h: 5, d: 0.65, t: 2.0, r: 55 },
    { l: 47, c: "#10b981", w: 8, h: 6, d: 0.7, t: 1.8, r: -50 },
    { l: 55, c: "#f59e0b", w: 6, h: 9, d: 0.55, t: 2.1, r: 25 },
    { l: 62, c: "#3b82f6", w: 9, h: 5, d: 0.75, t: 2.4, r: -35 },
    { l: 70, c: "#ec4899", w: 5, h: 8, d: 0.6, t: 2.0, r: 65 },
    { l: 77, c: "#ef4444", w: 8, h: 5, d: 0.8, t: 1.9, r: -10 },
    { l: 85, c: "#8b5cf6", w: 7, h: 7, d: 0.45, t: 2.2, r: 50 },
    { l: 92, c: "#10b981", w: 5, h: 9, d: 0.15, t: 2.3, r: -70 },
];

const CIRCLE_R = 42;
const CIRCLE_CIRC = 2 * Math.PI * CIRCLE_R; // ~263.9

const SuccessAnimation: React.FC = () => (
    <Box
        sx={{
            position: "relative",
            width: "100%",
            height: 220,
            overflow: "hidden",
            mb: 1,
            flexShrink: 0,
        }}
    >
        {/* Confetti */}
        {PARTICLES.map((p, i) => (
            <Box
                key={i}
                sx={{
                    position: "absolute",
                    left: `${p.l}%`,
                    top: "-16px",
                    width: p.w,
                    height: p.h,
                    bgcolor: p.c,
                    borderRadius: p.h > p.w ? "2px" : "1px",
                    "@keyframes confettiFall": {
                        "0%": { transform: `translateY(0px) rotate(${p.r}deg)`, opacity: 1 },
                        "85%": { opacity: 1 },
                        "100%": {
                            transform: `translateY(240px) rotate(${p.r + 400}deg)`,
                            opacity: 0,
                        },
                    },
                    animation: `confettiFall ${p.t}s ease-in ${p.d}s both`,
                }}
            />
        ))}

        {/* Checkmark SVG centrado */}
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "@keyframes popIn": {
                    "0%": { transform: "scale(0.6)", opacity: 0 },
                    "60%": { transform: "scale(1.08)" },
                    "100%": { transform: "scale(1)", opacity: 1 },
                },
                animation: "popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) 0s both",
            }}
        >
            <Box
                component="svg"
                width={110}
                height={110}
                viewBox="0 0 100 100"
                sx={{ filter: "drop-shadow(0 6px 20px rgba(0,133,71,0.25))" }}
            >
                {/* Fondo del círculo */}
                <circle cx="50" cy="50" r={CIRCLE_R} fill="#f0fdf4" />

                {/* Círculo animado */}
                <Box
                    component="circle"
                    cx="50"
                    cy="50"
                    r={CIRCLE_R}
                    fill="none"
                    stroke="#008547"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    sx={{
                        strokeDasharray: CIRCLE_CIRC,
                        strokeDashoffset: CIRCLE_CIRC,
                        transformOrigin: "50px 50px",
                        transform: "rotate(-90deg)",
                        "@keyframes drawCircle": {
                            from: { strokeDashoffset: CIRCLE_CIRC },
                            to: { strokeDashoffset: 0 },
                        },
                        animation: "drawCircle 0.55s cubic-bezier(0.4,0,0.2,1) 0.15s forwards",
                    }}
                />

                {/* Check */}
                <Box
                    component="path"
                    d="M 27 52 L 43 68 L 73 32"
                    fill="none"
                    stroke="#008547"
                    strokeWidth="4.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    sx={{
                        strokeDasharray: 65,
                        strokeDashoffset: 65,
                        "@keyframes drawCheck": {
                            from: { strokeDashoffset: 65 },
                            to: { strokeDashoffset: 0 },
                        },
                        animation: "drawCheck 0.38s ease-out 0.72s forwards",
                    }}
                />
            </Box>
        </Box>
    </Box>
);

export const CheckoutFinishedResult: React.FC = () => {
    const { processStatus } = useCheckoutForm();
    const navigate = useNavigate();
    const isSuccess = processStatus.type === CheckoutProcessStatusType.FinishedSuccessfully;
    const isError = processStatus.type === CheckoutProcessStatusType.FinishedWithError;

    const contactInfo = window.APP_CONFIG[AppConfigFields.ContactData];
    const whatsappNumber = contactInfo?.whatsapp?.replace(/\D/g, "");

    const rawMessage = [
        "Hola! Quería consultar por mi pedido.",
        "",
        `* Número de orden: #${processStatus.orderNumber}`,
        `* Nombre: ${processStatus.customerName}`,
        `* Email: ${processStatus.customerEmail}`,
        `* Teléfono: ${processStatus.customerPhone}`,
        processStatus.deliveryAddress ? `* Dirección: ${processStatus.deliveryAddress}` : "",
        `* Código Postal: ${processStatus.zipCode}`,
        "",
        "Gracias!",
    ]
        .filter(Boolean)
        .join("\n");

    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(rawMessage)}`;

    return (
        <Stack alignItems="center" sx={{ py: 2 }}>
            {/* ── Éxito ── */}
            {isSuccess && (
                <>
                    <SuccessAnimation />

                    <Box
                        sx={{
                            "@keyframes fadeUp": {
                                from: { opacity: 0, transform: "translateY(14px)" },
                                to: { opacity: 1, transform: "translateY(0)" },
                            },
                            animation: "fadeUp 0.45s ease 1.1s both",
                            textAlign: "center",
                            width: "100%",
                        }}
                    >
                        <Typography
                            fontFamily="inherit"
                            fontSize="22px"
                            fontWeight={700}
                            color="#008547"
                            letterSpacing="-0.02em"
                        >
                            ¡Pedido registrado correctamente!
                        </Typography>

                        {processStatus.orderNumber && (
                            <Typography
                                fontFamily="inherit"
                                fontSize="15px"
                                fontWeight={500}
                                color="text.secondary"
                                sx={{ mt: 0.75 }}
                            >
                                Número de orden:{" "}
                                <Box
                                    component="span"
                                    sx={{ fontWeight: 700, color: "#1a1c1e", fontSize: "17px" }}
                                >
                                    #{processStatus.orderNumber}
                                </Box>
                            </Typography>
                        )}

                        {/* Productos comprados */}
                        {processStatus.items && processStatus.items.length > 0 && (
                            <Box sx={{ mt: 2.5, width: "100%", maxWidth: 420, mx: "auto" }}>
                                <Stack spacing={1}>
                                    {processStatus.items.map((item, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                p: 1.25,
                                                borderRadius: "12px",
                                                background: "rgba(0,133,71,0.04)",
                                                border: "1px solid rgba(0,133,71,0.10)",
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={item.image}
                                                alt={item.name}
                                                sx={{
                                                    width: 48,
                                                    height: 60,
                                                    objectFit: "cover",
                                                    borderRadius: "8px",
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                                                <Typography
                                                    fontFamily="inherit"
                                                    fontSize="12px"
                                                    fontWeight={600}
                                                    color="#1a1c1e"
                                                    sx={{
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.02em",
                                                        lineHeight: 1.3,
                                                    }}
                                                    noWrap
                                                >
                                                    {item.name}
                                                </Typography>
                                                {item.attributes && item.attributes.length > 0 && (
                                                    <Typography
                                                        fontFamily="inherit"
                                                        fontSize="11px"
                                                        color="#707882"
                                                        sx={{ mt: 0.25 }}
                                                    >
                                                        {item.attributes
                                                            .map((a) => a.value)
                                                            .join(", ")}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography
                                                fontFamily="'SF Mono', 'Fira Code', monospace"
                                                fontSize="11px"
                                                fontWeight={600}
                                                color="#707882"
                                                sx={{ flexShrink: 0 }}
                                            >
                                                ×{item.quantity}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {processStatus.description && (
                            <Typography
                                fontFamily="inherit"
                                fontSize="14px"
                                color="text.secondary"
                                sx={{ mt: 2, opacity: 0.85, maxWidth: 420, mx: "auto" }}
                            >
                                {processStatus.description}
                            </Typography>
                        )}
                    </Box>
                </>
            )}

            {/* ── Error ── */}
            {isError && (
                <Stack alignItems="center" spacing={1.5} sx={{ py: 2 }}>
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            bgcolor: "#fef2f2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <XIcon size={30} color="#d32f2f" />
                    </Box>
                    <Typography
                        fontFamily="inherit"
                        fontSize="18px"
                        fontWeight={600}
                        color="#d32f2f"
                    >
                        No pudimos procesar tu compra
                    </Typography>
                    {processStatus.description && (
                        <Typography
                            fontFamily="inherit"
                            fontSize="14px"
                            color="text.secondary"
                            sx={{ opacity: 0.8, textAlign: "center" }}
                        >
                            {processStatus.description}
                        </Typography>
                    )}
                </Stack>
            )}

            {/* ── Botones (éxito o error) ── */}
            {(isSuccess || isError) && (
                <Box
                    sx={{
                        "@keyframes fadeUp2": {
                            from: { opacity: 0, transform: "translateY(10px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: isSuccess ? "fadeUp2 0.4s ease 1.5s both" : "none",
                        mt: 4,
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        justifyContent: "center",
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={() => window.open(whatsappUrl, "_blank")}
                        startIcon={<WhatsApp />}
                        sx={{
                            backgroundColor: "#25D366",
                            "&&": { color: "#fff" },
                            fontFamily: "inherit",
                            fontWeight: 500,
                            textTransform: "none",
                            py: 1.5,
                            "&:hover": { backgroundColor: "#1ebe5d" },
                        }}
                    >
                        Contactar por WhatsApp
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => navigate("/")}
                        sx={{
                            backgroundColor: "#1a1c1e",
                            "&&": { color: "#fff" },
                            fontFamily: "inherit",
                            fontWeight: 500,
                            textTransform: "none",
                            py: 1.5,
                            "&:hover": { backgroundColor: "#333" },
                        }}
                    >
                        Volver a la tienda
                    </Button>
                </Box>
            )}
        </Stack>
    );
};
